import React, { useState, useEffect } from "react";
import Image from "next/image";
import { profleUpdate, sendOtp, verifyOtp } from "@/app/utilities/apis";
import Link from "next/link";

interface LoginSignupProps {
  open: boolean;
  onClose: () => void;
}

const LoginSignup: React.FC<LoginSignupProps> = ({ open, onClose }) => {
  const [mobile, setMobile] = useState("");
  // const [agree, setAgree] = useState(false);
  const [step, setStep] = useState<'mobile' | 'otp' | 'profile' | 'welcome'>('mobile');
  const [otp, setOtp] = useState(['', '', '', '']);
  const [resendTimer, setResendTimer] = useState(60);
  const [fullName, setFullName] = useState("");
  const [profession, setProfession] = useState("");
  const [showProfessionDropdown, setShowProfessionDropdown] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [otpError, setOtpError] = useState("");
  const [profileError, setProfileError] = useState("");
  const [otpAttempts, setOtpAttempts] = useState(0);
  const [isBlocked, setIsBlocked] = useState(false);
  const [blockTimer, setBlockTimer] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isGettingStarted, setIsGettingStarted] = useState(false);
  const professions = ["Student", "Engineer", "Doctor", "Business", "Other"];
  const filteredProfessions = profession
    ? professions.filter((p) => p.toLowerCase().includes(profession.toLowerCase()))
    : professions;

  const isMobile = typeof window !== "undefined" ? window.innerWidth < 768 : false;

  useEffect(() => {
    if (open) {
      setIsVisible(true);
    }
  }, [open]);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => {
      onClose();
    }, 300); // Match the transition duration
  };

  React.useEffect(() => {
    if (step === 'otp' && resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [step, resendTimer]);

  React.useEffect(() => {
    if (isBlocked && blockTimer > 0) {
      const timer = setTimeout(() => setBlockTimer(blockTimer - 1), 1000);
      return () => clearTimeout(timer);
    } else if (isBlocked && blockTimer === 0) {
      setIsBlocked(false);
      setOtpAttempts(0);
      setOtpError("");
    }
  }, [isBlocked, blockTimer]);

  const handleOtpChange = (index: number, value: string) => {
    if (/^\d?$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);
      // Focus next input
      if (value && index < 3) {
        const nextInput = document.getElementById(`otp-input-${index + 1}`);
        if (nextInput) (nextInput as HTMLInputElement).focus();
      }
    }
  };

  const handleMobileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(/\D/g, ""); // Only digits
    if (value.length > 10) value = value.slice(0, 10);
    if (value && (value[0] < '6')) value = '';
    setMobile(value);
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(/[^a-zA-Z\s]/g, ""); // Only alphabets and spaces
    if (value.length > 30) value = value.slice(0, 30);
    setFullName(value);
  };

  const handleProfessionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(/[^a-zA-Z\s]/g, ""); // Only alphabets and spaces
    if (value.length > 30) value = value.slice(0, 30);
    setProfession(value);
    setShowProfessionDropdown(true);
  };

  if (!open) return null;

  // we will call otp api function here which is sendOtp on click of continue button
  const handleContinue = async () => {
    if (step === 'mobile') {
      setIsSubmitting(true);
      const response = await sendOtp(mobile);
      setIsSubmitting(false);
      if (response.success) {
        setStep('otp');
      } else {
        // Handle error
      }
    } else if (step === 'otp') {
      // Verify OTP
    }
  };

  // we will call verify otp api function here on click of verify button
  const handleVerify = async () => {
    if (isBlocked) {
      setOtpError(`Too many failed attempts. Please try again in ${Math.floor(blockTimer / 60)}:${(blockTimer % 60).toString().padStart(2, '0')}`);
      return;
    }

    setIsVerifying(true);
    const otpCode = otp.join("");
    const response = await verifyOtp(mobile, otpCode);
    setIsVerifying(false);
    if (response.success) {
      // Handle success
      document.cookie = `token=${response.token}; path=/; secure; samesite=strict`;
      document.cookie = `user=${JSON.stringify(response.user)}; path=/; secure; samesite=strict`;
      setOtpError("");
      setOtpAttempts(0); // Reset attempts on success
      if(response.user.created){
        setStep("profile");
      }else{
        handleClose();
      }
    } else {
      const newAttempts = otpAttempts + 1;
      setOtpAttempts(newAttempts);
      
      if (newAttempts >= 5) {
        setIsBlocked(true);
        setBlockTimer(15 * 60); // 15 minutes in seconds
        setOtpError("Too many failed attempts. Please try again after 15 minutes.");
      } else {
        setOtpError(response.message || `Invalid OTP. ${5 - newAttempts} attempts remaining.`);
      }
    }
  };

  const handleResend = async () => {
    if (isBlocked) {
      setOtpError(`Too many failed attempts. Please try again in ${Math.floor(blockTimer / 60)}:${(blockTimer % 60).toString().padStart(2, '0')}`);
      return;
    }

    const response = await sendOtp(mobile);
    if (response.success) {
      setResendTimer(60);
      setOtp(['', '', '', '']);
      setOtpError("");
    } else {
      setOtpError(response.message || "Failed to resend OTP. Please try again.");
    }
  };

  // we will call profile update api function here on click of get started button
  const handleGetStarted = async () => {
    setIsGettingStarted(true);
    const response = await profleUpdate(fullName, profession);
    setIsGettingStarted(false);
    if (response.success) {
      document.cookie = `user=${JSON.stringify(response.user)}; path=/; secure; samesite=strict`;
      setProfileError("");
      setStep("welcome");
    } else {
      setProfileError(response.message || "Failed to update profile. Please try again.");
    }
  };

  const isValidMobile = mobile.length === 10 && mobile[0] >= '6';
  const isValidName = fullName.replace(/\s/g, '').length >= 3 && /^[a-zA-Z\s]{3,30}$/.test(fullName);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 bg-opacity-60 ">
      <div className={`bg-white shadow-lg w-full max-w-4xl flex overflow-hidden fixed bottom-0 rounded-t-2xl md:relative md:bottom-auto md:rounded-2xl h-auto md:h-[601px] transition-transform duration-300 ease-in-out ${isVisible ? 'translate-y-0' : 'translate-y-full'} md:translate-y-0`}>
        <div className="absolute top-[10px] left-1/2 -translate-x-1/2 w-[33px] h-[4px] rounded-[10px] bg-[#D9D9D9] z-[99] block md:hidden" onClick={handleClose} aria-label="Close"></div>
        {/* Left image section */}
        <div className="hidden md:block w-1/2 bg-gray-100 relative">
          <Image
            src={`${process.env.NEXT_PUBLIC_CDN_URL}lead-modal-img.webp`}
            alt="Car Junction Login"
            fill
            className="object-cover w-full h-full"
            sizes="(max-width: 768px) 100vw, 50vw"
            priority
          />
          <div className="absolute inset-0 flex items-center justify-center bg-black/60">
            <Image src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}car-junction-white.svg`} alt="Car Junction" width={192} height={80} />
          </div>
        </div>
        {/* Right form section */}
        <div className="w-full md:w-1/2 p-[44px_22px_16px] md:p-12 flex flex-col justify-center relative font-[Roboto]">
          <button
            className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 text-xl"
            onClick={handleClose}
            aria-label="Close"
          >
            ×
          </button>
          {step === 'mobile' ? (
            <>
              <h2 className="font-[Roboto] font-bold text-[20px] leading-8 mb-4 md:mb-2 md:font-semibold md:text-[24px] text-[#1F2937]">{`Login/Register`}</h2>
              <p className="font-[Roboto] mb-6 text-[#6B7280]  font-normal text-[14px] leading-[20px]">{`Enter your mobile number to get started.`}</p>
              <label className="block font-[Roboto] font-medium text-[14px] leading-[20px] text-[#6B7280] mb-2">{`Mobile number`}</label>
              <input
                type="text"
                placeholder="+91 | 999 999 9999"
                value={mobile}
                onChange={handleMobileChange}
                className="w-full px-4 py-3 mb-4 rounded-lg text-[#757575] leading-[20px]  focus:outline-none focus:border-2 focus:border-teal-600 bg-[#F3F4F6] font-[Roboto] font-normal"
                maxLength={10}
              />
              {/* <div className="flex items-center mb-4">
                <input
                  type="checkbox"
                  checked={agree}
                  onChange={e => setAgree(e.target.checked)}
                  className="mr-2 w-4 h-4 rounded-[4px] border-2 border-[#1C77F6] text-[#1C77F6] focus:ring-0 focus:outline-none"
                  id="agree"
                />
                <label htmlFor="agree" className="text-sm text-[12px] text-[#6B7280]">
                  {`I agree to the`} <Link href="/terms-and-condition" onClick={() => handleClose()} className="text-automotive-red underline">{`Terms & Conditions`}</Link>
                </label>
              </div> */}
              <button
                className="font-[Roboto] leading-[24px] w-full bg-[#01827C] text-white py-3 rounded-lg font-bold text-[16px] disabled:opacity-50 shadow-md"
                disabled={!isValidMobile || isSubmitting}
                onClick={() => handleContinue()}
              >
                {isSubmitting ? 'SUBMITTING...' : 'CONTINUE'}
              </button>
            </>
          ) : step === 'otp' ? (
            <>
              <h2 className="font-[Roboto] font-bold text-[20px] leading-8 mb-4 md:mb-2 md:font-semibold md:text-[24px] text-[#1F2937]">{`Verify your mobile number`}</h2>
              <p className="font-[Roboto] mb-6 text-[#6B7280]  font-normal text-[14px] leading-[20px]">{`We've sent a 4-digit code to your mobile number.`}</p>
              <div className="flex space-x-4 justify-between mb-6">
                {otp.map((digit, idx) => (
                  <input
                    key={idx}
                    id={`otp-input-${idx}`}
                    type="text"
                    maxLength={1}
                    value={digit}
                    onChange={e => handleOtpChange(idx, e.target.value)}
                    className="w-14 h-14 text-2xl text-center border border-gray-300 rounded-lg focus:outline-none focus:border-2 focus:border-teal-600"
                  />
                ))}
              </div>
              {otpError && <div className="text-red-500 text-sm mb-2 text-center">{otpError}</div>}
              <button
                className="font-[Roboto] leading-[24px] w-full bg-[#01827C] text-white py-3 rounded-lg font-bold text-[16px] disabled:opacity-50 shadow-md mb-4"
                disabled={otp.some(d => d === "") || isBlocked || isVerifying}
                onClick={() => handleVerify()}
              >
                {isVerifying ? 'VERIFYING...' : 'VERIFY'}
              </button>
              {/* <div className="text-center text-[14px] text-gray-600 mb-2 font-semibold">{`Didn't receive the code? Please wait...`}</div> */}
              <div className="font-[Roboto] text-center text-[14px] text-gray-600 mb-2 font-semibold">{`Didn't receive the code? `}<span className="text-[#9ca3af]">{`Please wait...`}</span>
              </div>

              {resendTimer > 0 ? (
                <div className="font-[Roboto] text-center text-[14px] text-gray-600 mb-2 font-semibold">{`Resend code in `}<span className="text-black">{resendTimer}</span>{` seconds`}</div>
              ) : (
                <div className="font-[Roboto] text-center text-sm mb-2">
                  <button className="text-[#3B82F6] font-semibold underline cursor-pointer" onClick={handleResend}>
                    Resend code
                  </button>
                </div>
              )}
              <div className="text-center text-sm mb-2 md:mb-6">
                <button className="text-[#3B82F6] font-semibold cursor-pointer" onClick={() => { setStep('mobile'); setOtp(['','','','']); setResendTimer(60); }}>
                  Change mobile number
                </button>
              </div>
            </>
          ) : step === 'profile' ? (
            <>
              <h2 className="font-[Roboto] font-bold text-[20px] leading-8 md:mb-2 md:font-semibold md:text-[24px] text-[#1F2937]">Tell us about yourself</h2>
              <p className="font-[Roboto] mb-6 text-[#6B7280]  font-normal text-[14px] leading-[20px]">This helps us personalize your experience.</p>
              <label className="block font-[Roboto] font-medium text-[14px] leading-[20px] text-[#6B7280] mb-2">Full Name<span className="text-red-500">*</span></label>
              <input
                type="text"
                placeholder="e.g., Ramesh Kumar"
                value={fullName}
                onChange={handleNameChange}
                className="w-full px-4 py-3 mb-4 border border-gray-300 rounded-lg focus:outline-none focus:border-2 focus:border-teal-600"
                maxLength={30}
              />
              {profileError && <div className="text-red-500 text-sm mb-2">{profileError}</div>}
              <label className="block font-[Roboto] font-medium text-[14px] leading-[20px] text-[#6B7280] mb-2">Profession</label>
              <div className="relative mb-6">
                <input
                  type="text"
                  placeholder="Select Your Profession"
                  value={profession}
                  onChange={handleProfessionChange}
                  onFocus={() => setShowProfessionDropdown(true)}
                  onBlur={() => setTimeout(() => setShowProfessionDropdown(false), 150)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-2 focus:border-teal-600 pr-10"
                  autoComplete="off"
                  maxLength={30}
                />
                {/* Dropdown arrow icon */}
                <span className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none text-gray-400">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>
                </span>
                {showProfessionDropdown && filteredProfessions.length > 0 && (
                  <ul className="absolute left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10  max-h-48 overflow-auto sm:top-full sm:mt-1 bottom-full mb-1 sm:mb-0 sm:bottom-auto">
                    {filteredProfessions.map((prof) => (
                      <li
                        key={prof}
                        className="px-4 py-2 cursor-pointer hover:bg-gray-100"
                        onMouseDown={() => { setProfession(prof); setShowProfessionDropdown(false); }}
                      >
                        {prof}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <button
                className="font-[Roboto] leading-[24px] w-full bg-[#01827C] text-white py-3 rounded-lg font-bold text-[16px] disabled:opacity-50 shadow-md mb-4"
                disabled={!isValidName || isGettingStarted}
                onClick={() => handleGetStarted()}
              >
                {isGettingStarted ? 'SUBMITTING...' : 'GET STARTED'}
              </button>
            </>
          ) : (
            <>
              <div className="flex flex-col items-center justify-center h-full">
                <div className="mb-6">
                  <span className="inline-flex items-center justify-center w-16 h-16 rounded-full">
                    <Image src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}verified.svg`} alt="Verified" width={84} height={81} />
                  </span>
                </div>
                <h2 className="font-[Roboto] font-bold text-[24px] leading-8 md:mb-2 md:font-semibold md:text-[30px] text-[#1F2937]">Welcome, {fullName.split(" ")[0] || fullName} </h2>
                <p className="font-[Roboto] mb-8 text-[#6B7280]  font-normal text-[14px] leading-[20px]">Your account has been successfully created.</p>
                <div className="w-full flex flex-col gap-4">
                  <Link href="/popular-cars" onClick={() => handleClose()} className="w-full block bg-[#01827C] text-white py-3 rounded-lg font-bold text-lg shadow-md text-center" prefetch={false}>Popular Cars</Link>
                  <Link href="/compare-cars" onClick={() => handleClose()} className="w-full block bg-white border border-gray-300 text-gray-800 py-3 rounded-lg font-bold text-lg shadow-sm text-center" prefetch={false}>Compare</Link>
                  <Link href="/brands" onClick={() => handleClose()} className="w-full block bg-white border border-gray-300 text-gray-800 py-3 rounded-lg font-bold text-lg shadow-sm text-center" prefetch={false}>Brands</Link>
                </div>
              </div>
            </>
          )}
          {((isMobile && step === 'mobile') || (!isMobile && step !== 'welcome')) && (
            <div className="mt-8 md:mt-auto text-center text-xs text-gray-500">
              By proceeding, you confirm, consent and agree to the <Link prefetch={false} href="/privacy-policy" onClick={() => handleClose()} className="font-[Roboto] font-normal text-[12px] leading-[16px] text-[#01827C]">Privacy Policy</Link> and <Link prefetch={false} href="/terms-and-condition" onClick={() => handleClose()} className="font-[Roboto] font-normal text-[12px] leading-[16px] text-[#01827C]">Terms & Condition</Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginSignup;